import React, { useEffect, useRef } from 'react';

const FeatureTabs = ({
  title,
  desc,
  features,
  activeIndex = 0,
  onTabChange,
  showImage = true,
  imageHeight = 'h-full',
  containerClassName = '',
  gridCols = 'md:grid-cols-3',
  DocumentationHeading,
}) => {
  const scrollRef = useRef(null);

  // Scroll the strip when activeIndex changes programmatically — never scrolls the page
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const card = container.children[activeIndex];
    if (card) {
      container.scrollTo({
        left: card.offsetLeft - container.offsetLeft,
        behavior: 'smooth',
      });
    }
  }, [activeIndex]);

  // Detect manual swipe and sync activeIndex back to parent
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScrollEnd = () => {
      const firstCard = container.children[0];
      if (!firstCard) return;
      const cardWidth = firstCard.offsetWidth + 16; // 16px = gap-4
      const index = Math.round(container.scrollLeft / cardWidth);
      const clampedIndex = Math.max(0, Math.min(index, features.length - 1));
      if (onTabChange) {
        onTabChange(clampedIndex);
      }
    };

    // Use scrollend if supported, otherwise fallback to scroll + timeout
    if ('onscrollend' in window) {
      container.addEventListener('scrollend', handleScrollEnd);
      return () => container.removeEventListener('scrollend', handleScrollEnd);
    } else {
      let timer;
      const handleScroll = () => {
        clearTimeout(timer);
        timer = setTimeout(handleScrollEnd, 150);
      };
      container.addEventListener('scroll', handleScroll);
      return () => {
        clearTimeout(timer);
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [onTabChange, features]);

  const handleTabClick = (index) => {
    if (onTabChange) {
      onTabChange(index);
    }
  };

  const TabCard = ({ feature, index }) => (
    <div
      onClick={() => handleTabClick(index)}
      className="flex flex-col gap-4 text-left cursor-pointer group h-full"
    >
      {/* Top Bar */}
      <div
        className={`h-[4px] w-full rounded-full transition-all duration-300 ${
          index === activeIndex
            ? 'bg-[#EE2B2A]'
            : 'bg-[#35373A] group-hover:bg-[#EE2B2A]/50'
        }`}
      />

      {/* Title */}
      <div
        className={`text-[20px] font-bold font-jakarta transition-all duration-300 ${
          index === activeIndex
            ? 'text-white'
            : 'text-white group-hover:text-[#EE2B2A]'
        }`}
      >
        {feature.title}
      </div>

      {/* Description */}
      <div
        className={`text-md md:text-lg font-light font-jakarta leading-relaxed transition-all duration-300 whitespace-normal break-words ${
          index === activeIndex
            ? 'text-[#ffffff]'
            : 'text-[#717173] group-hover:text-[#b0b0b0]'
        }`}
      >
        {feature.desc}
      </div>
    </div>
  );

  return (
    <div
      className={`w-full flex flex-col gap-4 items-center my-6 md:my-10 ${containerClassName}`}
    >
      {/* DocumentationHeading */}
      {DocumentationHeading && (
        <DocumentationHeading title={title} desc={desc} />
      )}

      {/* Active Feature Image */}
      {showImage && features[activeIndex]?.img && (
        <img
          src={features[activeIndex].img}
          alt={features[activeIndex].title || 'feature'}
          className={`w-full ${imageHeight} object-contain`}
        />
      )}

      {/* ─── MOBILE: Carousel ─────────────────────────────────── */}
      <div className="flex md:hidden flex-col w-full gap-4">
        {/* Cards scroll container */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto gap-4 w-full pb-2 scrollbar-hide snap-x snap-mandatory"
        >
          {features?.map((feature, index) => (
            <div
              key={index}
              className="min-w-[82vw] max-w-[82vw] flex-shrink-0 snap-center"
            >
              <TabCard feature={feature} index={index} />
            </div>
          ))}
        </div>

        {/* Dot Indicators */}
        {/* <div className="flex items-center justify-center gap-2 mt-1">
          {features?.map((_, index) => (
            <button
              key={index}
              onClick={() => handleTabClick(index)}
              className={`rounded-full transition-all duration-300 ${
                index === activeIndex
                  ? 'w-5 h-2 bg-[#EE2B2A]'
                  : 'w-2 h-2 bg-[#35373A] hover:bg-[#EE2B2A]/50'
              }`}
            />
          ))}
        </div> */}
      </div>

      {/* ─── DESKTOP: Grid — exactly as before ────────────────── */}
      <div className={`hidden md:grid grid-cols-1 ${gridCols} gap-12 w-full`}>
        {features?.map((feature, index) => (
          <div key={index} className="max-w-[460px]">
            <TabCard feature={feature} index={index} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeatureTabs;

export const CarouselControls = ({
  activeIndex,
  total,
  onChange,
  className = '',
}) => {
  const handlePrev = () =>
    onChange(activeIndex > 0 ? activeIndex - 1 : total - 1);
  const handleNext = () =>
    onChange(activeIndex < total - 1 ? activeIndex + 1 : 0);

  const ArrowButton = ({ onClick, direction }) => (
    <button
      onClick={onClick}
      className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1e2024] border border-[#35373A] text-white hover:border-[#EE2B2A] hover:text-[#EE2B2A] transition-all duration-200"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d={direction === 'prev' ? 'M9 11L5 7L9 3' : 'M5 3L9 7L5 11'}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  return (
    <div className={`flex items-center justify-end gap-3 ${className}`}>
      <ArrowButton onClick={handlePrev} direction="prev" />
      <ArrowButton onClick={handleNext} direction="next" />
    </div>
  );
};
